import React, { useCallback, useContext, useState } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { NetworkStatus } from 'apollo-client';

import type { UIAnalyticsEventHandler, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsListener } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';

import type { BannerStateContainer } from '@confluence/banners';
import { ContentTypesHeader } from '@confluence/content-types-header';
import { ContentTypesHeaderPlaceholder } from '@confluence/content-types-header-base';
import { ExperienceTrackerContext, WHITEBOARD_EXPERIENCE } from '@confluence/experience-tracker';
import { PageSegmentLoadStart } from '@confluence/browser-metrics';
import { ContentViewedEvent } from '@confluence/content-body';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { WHITEBOARD_BOARD } from '@confluence/named-routes';
import { NotFoundContained } from '@confluence/content-prerequisites';
import {
	NEW_WHITEBOARD_QUERY_PARAM,
	WHITEBOARD_IFRAME_TITLE,
	WHITEBOARD_LINKED_OBJECT,
	WHITEBOARD_LOCALE,
	WHITEBOARD_CREATION_PAYWALL_SOURCE_PREFIX,
	FOCUSED_COMMENT_ID,
	useThemeLoaded,
} from '@confluence/whiteboard-utils';
import { BannerContext } from '@confluence/banners';
import { WhiteboardPaywallReadOnlyBanner } from '@confluence/change-edition/entry-points/WhiteboardPaywallReadOnlyBanner';
import { ContentFeedbackService } from '@confluence/content-types-bridge/entry-points/content-types-feedback-service';
import { GlobalFeedbackCollectorModal } from '@confluence/content-types-bridge/entry-points/GlobalFeedbackCollectorModal';
import { ContentTypesFlagService } from '@confluence/content-types-bridge/entry-points/content-types-flag-service';
import {
	WhiteboardResolvedCommentProvider,
	useIsVersionHistoryEnabled,
} from '@confluence/content-types-utils';
import { withFlags } from '@confluence/flags';
import { WhiteboardVersionHistory } from '@confluence/version-history';
import { useIsSidePanelOpen } from '@confluence/side-panel-state-container';

import { whiteboardShellStyle, whiteboardHeightContainer } from './whiteboard-shell-styles';
import { useWhiteboardParams } from './whiteboard-params';
import { getWhiteboardVersion } from './whiteboard-version';
import { WhiteboardExitVersion } from './WhiteboardVersion';
import { useWhiteboardBridgeAPI } from './useWhiteboardBridgeAPI';
import { useResizeBanner } from './useResizeBanner';
import { WHITEBOARD_VIEW_PAGE_CONTENT_SEGMENT, StartWhiteboardPageLoad } from './perf.config';
import { useSpace } from './graphql/useSpace';
import { useWhiteboardHeaderActions } from './useWhiteboardHeaderActions';
import { MobileWebView } from './WhiteboardMobileWebView';
import { getWhiteboardSpot } from './get-whiteboard-spot';
import { WhiteboardEditionUpgradeService } from './services/whiteboard-edition-upgrade-service';
import { WhiteboardEditionUpgradeCTA } from './WhiteboardEditionUpgradeCTA';
import type { WhiteboardShellProps } from './whiteboard-shell-types';
import { useOnboardingService } from './useOnboardingService';
import { useWhiteboardQueryParamState } from './useWhiteboardQueryParamState';
import { WhiteboardResolvedCommentsService } from './services/whiteboard-resolved-comment-service';
import type { ResolveWhiteboardCommentIds } from './services/whiteboard-resolved-comment-service';
import { WhiteboardInternalModalService } from './services/whiteboard-internal-modal-service';
import { WhiteboardInternalModal } from './WhiteboardInternalModal';
import { useIntegrationService } from './useIntegrationService';
import { WhiteboardEventService } from './services/whiteboard-event-service';

const PAYWALL_READ_ONLY_BANNER_ANALYTICS_SOURCE = `${WHITEBOARD_CREATION_PAYWALL_SOURCE_PREFIX}readOnlyBanner`;

const contentHeaderWrapperStyles = xcss({
	borderBottomWidth: 'border.width',
	borderBottomStyle: 'solid',
	borderBottomColor: 'color.border',
	backgroundColor: 'elevation.surface',
});

const iframeAndSidePanelWrapperStyle = xcss({
	position: 'relative',
	overflow: 'hidden',
});

export const WhiteboardShellComponent = ({
	space: spaceFromProps,
	spaceKey,
	contentId,
	contentReferenceId,
	externalShareContent,
	loading,
	queryNetworkStatus,
	refetch,
	allowedFeatures,
	displayPaywallViewOnlyBanner,
	shareToken,
	flags,
}: WhiteboardShellProps) => {
	const isSSR = Boolean(process.env.REACT_SSR);
	const { isNotLoaded, onThemeLoaded } = useThemeLoaded(isSSR);

	const {
		bannerHeight: whiteboardReadOnlyBannerHeight,
		setBannerElement: setWhiteboardReadOnlyBannerElement,
	} = useResizeBanner(isSSR);

	const { bannerHeight: whiteboardHeaderHeight, setBannerElement: setWhiteboardHeaderElement } =
		useResizeBanner(isSSR);

	const isWhiteboardHeaderPinned = useIsVersionHistoryEnabled('whiteboard');

	const isLoadingNewContent = loading && queryNetworkStatus !== NetworkStatus.refetch;
	const params = useWhiteboardParams();
	const version = getWhiteboardVersion(params);
	const serverSpot = getWhiteboardSpot(params, 'canvas-server');
	const relaySpot = getWhiteboardSpot(params, 'canvas-relay');

	const shouldShowMobileWebWhiteboard = shouldShowMobileWeb() && !params.isMobileAppEmbed;
	const contentActions = useWhiteboardHeaderActions();

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { includeTopNav } = useContext(BannerContext);

	const [editionUpgradeService] = useState(() => new WhiteboardEditionUpgradeService());

	const [feedbackService] = useState(() => new ContentFeedbackService());

	const [resolvedCommentService] = useState(() => new WhiteboardResolvedCommentsService());
	const [eventService] = useState(() => new WhiteboardEventService());

	const [whiteboardInternalModalService] = useState(() => new WhiteboardInternalModalService());

	const [flagService] = useState(() => new ContentTypesFlagService(flags));

	const isSidePanelOpen = useIsSidePanelOpen();

	const reopenResolvedCommentCallback = useCallback(
		(commentIds: ResolveWhiteboardCommentIds) => {
			resolvedCommentService.onReopenResolvedComment(commentIds);
		},
		[resolvedCommentService],
	);

	const space = useSpace(spaceKey, spaceFromProps);
	const integrationService = useIntegrationService(flags, contentId);

	const updateWhiteboardHeaderEvents: UIAnalyticsEventHandler = (event: UIAnalyticsEvent) => {
		const newData = {
			objectType: 'whiteboard',
		};

		const newAttributes = {
			contentType: 'whiteboard',
		};

		event.update((payload) => {
			const mergedAttributes = {
				...payload.data?.attributes,
				...newAttributes,
			};

			return {
				...payload,
				data: {
					...payload.data,
					...newData,
					attributes: mergedAttributes,
				},
			};
		});
	};

	const isNewWhiteboard = Boolean(
		useWhiteboardQueryParamState(contentId, NEW_WHITEBOARD_QUERY_PARAM),
	);

	const onboardingService = useOnboardingService({ isNewWhiteboard });

	const { initialUrl } = useWhiteboardBridgeAPI({
		version,
		serverSpot,
		relaySpot,
		space,
		spaceKey,
		contentId,
		// use "undefined" value here allows us to render whiteboard skeleton loading state
		// while WhiteboardUnifiedQuery is loading
		referenceId: isLoadingNewContent ? undefined : contentReferenceId,
		shareToken,
		params,
		refetch,
		services: {
			editionUpgradeService,
			feedbackService,
			onboardingService,
			resolvedCommentService,
			whiteboardInternalModalService,
			eventService,
			flagService,
			integrationService,
		},
		whiteboardLocale: params[WHITEBOARD_LOCALE],
		whiteboardLinkedObject: params[WHITEBOARD_LINKED_OBJECT],
		focusedCommentId: params[FOCUSED_COMMENT_ID],
		onThemeLoaded,
	});

	const handleCloseTitleSpotlight = useCallback(() => {
		onboardingService.startOnboarding('spotlight-sticky-creator');
	}, [onboardingService]);

	const handleRegisterOnShowHeaderSpotlight = useCallback(
		(cb: () => void) => {
			onboardingService.onShowHeaderSpotlight(cb);
		},
		[onboardingService],
	);

	const handleRegisterOnShowTitleSpotlight = useCallback(
		(cb: () => void) => {
			onboardingService.onShowTitleSpotlight(cb);
		},
		[onboardingService],
	);

	const handleRegisterOnShowCollaboratorSpotlight = useCallback(
		(cb: () => void) => {
			onboardingService.onShowCollaboratorSpotlight(cb);
		},
		[onboardingService],
	);

	if (!loading && !contentReferenceId) {
		experienceTracker.fail({
			name: WHITEBOARD_EXPERIENCE,
			error: new Error('WhiteboardUnifiedQuery did not return valid referenceId'),
		});

		return <NotFoundContained route={WHITEBOARD_BOARD} spaceKey={spaceKey} />;
	}

	const showContentTypesHeader =
		!process.env.REACT_SSR && !isLoadingNewContent && !params.isSmartLinkEmbed;

	const showContentTypesHeaderPlaceholder = isLoadingNewContent && !params.isSmartLinkEmbed;

	return (
		<>
			<StartWhiteboardPageLoad contentId={contentId} />
			<Subscribe to={whiteboardHeightContainer}>
				{(bannerState: BannerStateContainer) => (
					<>
						{displayPaywallViewOnlyBanner && (
							<WhiteboardPaywallReadOnlyBanner
								ref={setWhiteboardReadOnlyBannerElement}
								analyticsSource={PAYWALL_READ_ONLY_BANNER_ANALYTICS_SOURCE}
							/>
						)}
						{showContentTypesHeaderPlaceholder && (
							<ContentTypesHeaderPlaceholder isFloating={!isWhiteboardHeaderPinned} />
						)}
						{showContentTypesHeader && (
							<WhiteboardResolvedCommentProvider callback={reopenResolvedCommentCallback}>
								<AnalyticsListener onEvent={updateWhiteboardHeaderEvents}>
									{isWhiteboardHeaderPinned ? (
										<Box
											testId="pinned-content-header"
											xcss={contentHeaderWrapperStyles}
											ref={setWhiteboardHeaderElement}
										>
											<ContentTypesHeader
												contentId={contentId}
												contentActions={contentActions}
												externalShareContent={externalShareContent}
												allowedFeatures={allowedFeatures}
												isFloating={!isWhiteboardHeaderPinned}
												onCloseTitleSpotlight={handleCloseTitleSpotlight}
												registerShowTitleSpotlightHandler={handleRegisterOnShowTitleSpotlight}
												handleRegisterOnShowHeaderSpotlight={handleRegisterOnShowHeaderSpotlight}
												handleRegisterOnShowCollaboratorSpotlight={
													handleRegisterOnShowCollaboratorSpotlight
												}
												isWhiteboardHeaderPinned={isWhiteboardHeaderPinned}
											/>
										</Box>
									) : (
										<ContentTypesHeader
											contentId={contentId}
											contentActions={contentActions}
											externalShareContent={externalShareContent}
											allowedFeatures={allowedFeatures}
											isFloating={!isWhiteboardHeaderPinned}
											onCloseTitleSpotlight={handleCloseTitleSpotlight}
											registerShowTitleSpotlightHandler={handleRegisterOnShowTitleSpotlight}
											handleRegisterOnShowHeaderSpotlight={handleRegisterOnShowHeaderSpotlight}
											handleRegisterOnShowCollaboratorSpotlight={
												handleRegisterOnShowCollaboratorSpotlight
											}
										/>
									)}
								</AnalyticsListener>
							</WhiteboardResolvedCommentProvider>
						)}

						{shouldShowMobileWebWhiteboard ? (
							<MobileWebView bannerState={bannerState} />
						) : (
							<>
								<PageSegmentLoadStart metric={WHITEBOARD_VIEW_PAGE_CONTENT_SEGMENT} />
								{isWhiteboardHeaderPinned ? (
									<Box xcss={iframeAndSidePanelWrapperStyle}>
										<iframe
											title={WHITEBOARD_IFRAME_TITLE}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
											style={whiteboardShellStyle(
												bannerState.getTotalHeight(includeTopNav) +
													whiteboardReadOnlyBannerHeight +
													whiteboardHeaderHeight,
												isNotLoaded,
												isSidePanelOpen,
											)}
											src={initialUrl}
											frameBorder={0}
										/>
										{isSidePanelOpen && (
											<WhiteboardVersionHistory
												headerHeight={
													bannerState.getTotalHeight(includeTopNav) +
													whiteboardReadOnlyBannerHeight +
													whiteboardHeaderHeight
												}
											/>
										)}
									</Box>
								) : (
									<iframe
										title={WHITEBOARD_IFRAME_TITLE}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
										style={whiteboardShellStyle(
											bannerState.getTotalHeight(includeTopNav) +
												whiteboardReadOnlyBannerHeight +
												whiteboardHeaderHeight,
											isNotLoaded,
											isSidePanelOpen,
										)}
										src={initialUrl}
										frameBorder={0}
									/>
								)}
								{!process.env.REACT_SSR && <ContentViewedEvent contentId={contentId} />}
							</>
						)}
					</>
				)}
			</Subscribe>
			<WhiteboardEditionUpgradeCTA editionUpgradeService={editionUpgradeService} />
			<GlobalFeedbackCollectorModal feedbackService={feedbackService} />
			<WhiteboardInternalModal whiteboardInternalModalService={whiteboardInternalModalService} />
			<WhiteboardExitVersion version={version} serverSpot={serverSpot} relaySpot={relaySpot} />
		</>
	);
};

export const WhiteboardShell = withFlags(WhiteboardShellComponent);
